import styled, { css, keyframes } from "styled-components";

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

export const SectionHeader = styled.div`
  margin-bottom: 25px;
  padding-bottom: 15px;
  border-bottom: 2px solid var(--accent-light);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
`;

export const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  text-transform: uppercase;
  letter-spacing: 1px;
  margin: 0;
`;

export const SectionActions = styled.div`
  display: flex;
  gap: 15px;
`;

export const Button = styled.button`
  padding: 12px 25px;
  border: none;
  border-radius: 20px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  cursor: pointer;
  transition: transform 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;

  ${(props) =>
    props.$small &&
    `
    padding: 8px 16px;
    font-size: 12px;
  `}

  ${(props) =>
    props.$primary &&
    `
    background: var(--gradient-primary);
    color: var(--surface);
    
    &:hover:not(:disabled) {
      background: var(--accent-dark);
      color: var(--surface);
      transform: translateY(-2px);
    }
  `}

  ${(props) =>
    props.$secondary &&
    `
    background: transparent;
    color: var(--text-secondary);

    
    &:hover:not(:disabled) {
      background: var(--accent-light);

      color: var(--accent);
      transform: translateY(-2px);
    }
  `}

     svg {
    animation: ${(props) =>
      props.$loading
        ? css`
            ${spin} 1s ease-in-out infinite
          `
        : "none"};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

export const Filters = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 25px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

// Table Components
export const DataTableScroll = styled.div`
  width: 100% !important;
  overflow-x: auto !important;
  overflow-y: visible !important;
  border-radius: 15px;
  box-shadow: var(--shadow-soft);
  max-width: none !important;
  min-width: 0 !important;
  flex-shrink: 0 !important;
`;

export const DataTable = styled.div`
  display: grid;
  grid-template-columns: 250px 300px 180px 100px 1fr;
  min-width: 986px;

  background: var(--surface);
  border-radius: 15px;
  border: 2px solid var(--accent-light);
  overflow: hidden;
  animation: ${slideIn} 0.6s ease-out;
`;

export const TableHeader = styled.div`
  padding: 15px 12px;
  border-bottom: 2px solid var(--accent-light);
  background-color: var(--accent);
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  white-space: nowrap;
  letter-spacing: 1px;
  color: white;
  text-align: ${(props) => props.$align || "left"};
  display: flex;
  align-items: center;
  justify-content: ${(props) => props.$align || "flex-start"};
`;

export const TableRow = styled.div`
  display: contents;
  cursor: pointer;
  &:hover > div {
    background: rgba(124, 14, 51, 0.05);
  }
`;

export const TableCell = styled.div`
  padding: 15px 20px;
  text-align: left;
  border-bottom: 1px solid var(--accent-light);
  white-space: nowrap;
  font-size: 14px;
  color: var(--text-primary);
  display: flex;
  align-items: center;
`;

export const LoadingCell = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  padding: 20px;
`;

export const EmptyCell = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  padding: 20px;
  color: var(--text-muted);
`;

export const StatusBadge = styled.span`
  background: ${(props) => props.color};
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  white-space: nowrap;
  letter-spacing: 0.5px;
`;

export const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  div {
    line-height: 1.4;
  }

  strong {
    color: var(--text);
    font-weight: 500;
  }

  small {
    color: var(--text-muted);
    font-size: 12px;
  }
`;

export const CourseInfo = styled.div`
  font-weight: 500;
  color: var(--text);
`;

export const VideoLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--accent);
  text-decoration: none;
  font-size: 13px;
  font-weight: 500;
  padding: 6px 12px;
  white-space: nowrap;
  border-radius: 6px;
  background: var(--background-soft);
  transition: all 0.3s ease;

  &:hover {
    background: var(--accent);
    color: var(--surface);
    transform: translateY(-1px);
  }
`;

export const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

export const ActionButton = styled.button`
  background: ${(props) => {
    switch (props.type) {
      case "view":
        return "#3b82f6";
      case "delete":
        return "#ef4444";
      default:
        return "#6b7280";
    }
  }};
  color: white;
  border: none;
  padding: 8px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.2);
  }
`;

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: ${fadeIn} 0.3s ease-out;
  backdrop-filter: blur(5px);
`;

export const ModalContent = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: var(--surface);
  border-radius: 20px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: ${slideIn} 0.3s ease-out;

  @media (max-width: 768px) {
    width: 95%;
    margin: 20px;
  }
`;

export const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 25px 30px 20px;
  border-bottom: 1px solid var(--accent-light);

  @media (max-width: 768px) {
    padding: 20px 20px 15px;
  }
`;

export const ModalTitle = styled.h3`
  color: var(--accent);
  font-size: 20px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

export const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-muted);
  font-size: 18px;
  cursor: pointer;
  padding: 5px;
  border-radius: 50%;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: var(--background-soft);
    color: var(--text);
    transform: scale(1.1);
  }
`;

export const ApplicationDetails = styled.div`
  padding: 30px;

  > div {
    margin-bottom: 25px;

    h4 {
      color: var(--accent);
      margin-bottom: 10px;
      font-size: 16px;
    }

    p {
      margin-bottom: 8px;
      line-height: 1.5;
    }
  }

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

export const ReviewForm = styled.form`
  border-top: 1px solid var(--accent-light);
  padding-top: 25px;
`;

export const FormGroup = styled.div`
  margin-bottom: 20px;

  input {
    width: 100%;
    padding: 12px 15px;
    border: 2px solid var(--accent-light);
    border-radius: 10px;
    font-family: inherit;
    font-size: 14px;
    background: var(--background-soft);
    color: var(--text);
    transition: all 0.3s ease;

    &:focus {
      outline: none;
      border-color: var(--accent);
      box-shadow: 0 0 0 3px rgba(122, 14, 51, 0.1);
    }
  }
`;

export const Label = styled.label`
  display: block;
  color: var(--text);
  font-weight: 500;
  margin-bottom: 8px;
  font-size: 14px;
`;

export const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 15px;
  border: 2px solid var(--accent-light);
  border-radius: 10px;
  font-family: inherit;
  font-size: 14px;
  background: var(--background-soft);
  color: var(--text);
  transition: all 0.3s ease;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(122, 14, 51, 0.1);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

export const SubmitButton = styled.button`
  background: var(--gradient-primary);
  color: var(--surface);
  border: none;
  padding: 12px 25px;
  border-radius: 10px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(122, 14, 51, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;
